import { Box, Typography, images } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { common, deepPurple } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";

import type { FC, ReactNode } from "react";
import type { MalnutritionPatientSummary } from "../../types/Malnutrition";

const styles = {
  wrapper: css`
    background: url("${images.PatientBackground}") ${deepPurple[100]};
    background-size: cover;
    color: ${common.white};
    padding: 1rem 1rem 1.25rem 1rem;
    box-sizing: border-box;
  `,
  topRow: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
  `,
  iconButton: css`
    color: ${common.white};
    background-color: ${alpha(common.white, 0.15)};
    width: 1.75rem;
    height: 1.75rem;
  `,
};

const sxStyles = {
  name: {
    fontSize: "1.625rem",
    fontWeight: "bold",
    lineHeight: 1.2,
    wordBreak: "break-word",
    flex: 1,
    minWidth: 0,
  },
  demographics: {
    fontSize: "0.75rem",
    marginTop: "0.5rem",
    opacity: 0.95,
  },
  iconButtonRoot: {
    color: common.white,
    backgroundColor: alpha(common.white, 0.15),
    width: "1.75rem",
    height: "1.75rem",
    padding: "0.25rem",
    "&:hover": { backgroundColor: alpha(common.white, 0.25) },
  },
};

interface MalnutritionHeaderProps {
  patient: MalnutritionPatientSummary;
  onExpand?: () => void;
  onClose?: () => void;
  /** Override the close affordance when the host provides one (e.g., drawer chrome). */
  closeButtonCmpt?: ReactNode;
}

const MalnutritionHeader: FC<MalnutritionHeaderProps> = ({
  patient,
  onExpand,
  onClose,
  closeButtonCmpt,
}) => {
  // Build from non-empty segments so coalesced-empty fields (age/gender/bed/
  // unit/mrn) don't leave dangling separators like "Bed 412A ·  · 4 East".
  const genderInitial = patient.gender
    ? patient.gender.charAt(0).toUpperCase()
    : "";
  const ageGender = `${patient.age ?? ""}${genderInitial}`;
  const demographics = [
    patient.current_bed ? `Bed ${patient.current_bed}` : "",
    ageGender,
    patient.current_unit,
    patient.mrn ? `MRN ${patient.mrn}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box className={styles.wrapper}>
      <Box className={styles.topRow}>
        <Typography sx={sxStyles.name}>{patient.full_name}</Typography>
        <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {onExpand && (
            <IconButton
              size="small"
              onClick={onExpand}
              aria-label="Expand"
              sx={sxStyles.iconButtonRoot}
            >
              <OpenInFullIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
          {closeButtonCmpt ??
            (onClose && (
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close"
                sx={sxStyles.iconButtonRoot}
              >
                <CloseIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            ))}
        </Box>
      </Box>
      <Typography sx={sxStyles.demographics}>{demographics}</Typography>
    </Box>
  );
};

export default MalnutritionHeader;
